import { useState } from 'react';
import imgBadgeScan from '../images/badge-scan-flow.png';
import imgReportingPie from '../images/reporting-pie-charts.png';
import imgReportingFull from '../images/reporting-full-dashboard.png';
import imgDashboardGauges from '../images/dashboard-gauges.png';
import imgDashboardGoals from '../images/dashboard-goals.png';
import imgSmsOutreach from '../images/sms-outreach.png';

function Home() {
  const [activeTab, setActiveTab] = useState('lead-collection');

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const capabilities = [
    { id: 'lead-collection', label: 'Lead Collection' },
    { id: 'touchpoints', label: 'Multiple Touchpoints' },
    { id: 'badge-scanning', label: 'Badge Scanning' },
    { id: 'seamless-experience', label: 'Seamless Experience' },
    { id: 'reporting', label: 'Smarter Reporting' },
    { id: 'dashboards', label: 'Live Dashboards' },
    { id: 'integrations', label: 'Custom Integrations' },
    { id: 'ios-app', label: 'iOS Native App' },
  ];

  const featureCards = [
    { id: 'lead-collection', label: 'Lead Collection', desc: 'Mobile-optimized, branded registration workflows with branching logic and multilingual support.' },
    { id: 'touchpoints', label: 'Multiple Touchpoints', desc: 'QR codes, digital kiosks, and brand ambassador interactions — online and offline.' },
    { id: 'badge-scanning', label: 'Badge Scanning', desc: 'Instant low-touch data capture from event badges with integrations for all major platforms.' },
    { id: 'seamless-experience', label: 'Seamless Experience', desc: 'Progressive profiling builds participant profiles across activations without repeating questions.' },
    { id: 'reporting', label: 'Smarter Reporting', desc: 'Track full user journeys, interaction sources, and unique vs. repeat engagement.' },
    { id: 'dashboards', label: 'Live Dashboards', desc: 'Real-time registrant counts, opt-in rates, survey breakdowns, and team performance.' },
    { id: 'integrations', label: 'Custom Integrations', desc: 'API connections, CRM sync, and personalized triggered email or SMS outreach.' },
    { id: 'ios-app', label: 'iOS Native App', desc: 'On/offline data capture, multi-language support, and real-time team leaderboards.' },
  ];

  return (
    <>
      {/* Hero Section */}
      <section id="hero" className="hero">
        <div className="hero-content">
          <h1 className="hero-title">Event Data Capture & Lead Collection</h1>
          <p className="hero-subtitle">
            25+ years of expertise powering smarter activations — from badge scanning to live dashboards to personalized follow-up.
          </p>
          <button onClick={() => scrollToSection('features')} className="cta-button">
            See What We Do
          </button>
        </div>
      </section>

      {/* Feature Cards */}
      <section id="features" className="features-section">
        <div className="container">
          <h2 className="section-title">Everything You Need to Capture, Connect & Convert</h2>
          <p className="features-intro">
            Click any capability to explore it in detail.
          </p>
          <div className="feature-cards-grid">
            {featureCards.map((f) => (
              <button
                key={f.id}
                className="feature-card-btn"
                onClick={() => { setActiveTab(f.id); scrollToSection('capabilities'); }}
              >
                <h3 className="feature-card-label">{f.label}</h3>
                <p className="feature-card-desc">{f.desc}</p>
                <span className="feature-card-cta">Explore →</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section id="capabilities" className="capabilities">
        <div className="container">
          <h2 className="section-title">Our Capabilities</h2>
          
          <div className="tabs">
            {capabilities.map((cap) => (
              <button
                key={cap.id}
                className={`tab ${activeTab === cap.id ? 'active' : ''}`}
                onClick={() => setActiveTab(cap.id)}
              >
                {cap.label}
              </button>
            ))}
          </div>

          <div className="tab-content">
            {/* Lead Collection */}
            {activeTab === 'lead-collection' && (
              <div className="capability-content">
                <h3>Lead Collection</h3>
                <p className="capability-intro">
                  Our microsites are mobile optimized, totally customizable, and deliver real-time insights.
                </p>
                <div className="features-grid">
                  <div className="feature">
                    <h4>Custom Branded Registration Workflows</h4>
                    <p>Mobile-optimized, fully customizable registration experiences that reflect your brand.</p>
                  </div>
                  <div className="feature">
                    <h4>Personalized Question Sets</h4>
                    <p>Custom question sets with branching logic to capture the most relevant data.</p>
                  </div>
                  <div className="feature">
                    <h4>Multilingual Features</h4>
                    <p>Automatically detects user's browser language for seamless global events.</p>
                  </div>
                  <div className="feature">
                    <h4>Easy Input</h4>
                    <p>Address autocomplete, browser autofill, and email validation for frictionless data entry.</p>
                  </div>
                  <div className="feature">
                    <h4>Seamless User Experience</h4>
                    <p>Pre-registration online or in person, with easy check-in and check-out processes.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Multiple Touchpoints */}
            {activeTab === 'touchpoints' && (
              <div className="capability-content">
                <h3>Multiple Touchpoints</h3>
                <p className="capability-intro">
                  Whether scanning a QR code, chatting with a brand ambassador, or using a digital kiosk, 
                  customers can easily connect with your brand, on or offline.
                </p>
                <div className="features-grid">
                  <div className="feature">
                    <h4>QR Code Scanning</h4>
                    <p>Quick and easy mobile registration via QR codes placed throughout your event.</p>
                  </div>
                  <div className="feature">
                    <h4>Brand Ambassador Interactions</h4>
                    <p>Empower your team to capture leads directly through face-to-face conversations.</p>
                  </div>
                  <div className="feature">
                    <h4>Digital Kiosks</h4>
                    <p>Self-service registration stations for high-traffic areas and autonomous engagement.</p>
                  </div>
                  <div className="feature">
                    <h4>Online & Offline</h4>
                    <p>Seamlessly works both online and offline, syncing data when connectivity returns.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Badge Scanning */}
            {activeTab === 'badge-scanning' && (
              <div className="capability-content">
                <h3>Badge Scanning</h3>
                <p className="capability-intro">
                  Integrated into customizable lead capture flow for low-touch data collection during commercial events.
                </p>
                <div className="integrations-section">
                  <h4>Existing Integrations</h4>
                  <div className="integration-tags">
                    <span className="integration-tag">BadgeGuys</span>
                    <span className="integration-tag">CompuSystems</span>
                    <span className="integration-tag">Conexys</span>
                    <span className="integration-tag">Cvent</span>
                    <span className="integration-tag">LeadsBadgeScan</span>
                    <span className="integration-tag">Maritz</span>
                    <span className="integration-tag">VoiceHive</span>
                    <span className="integration-tag">...and more!</span>
                  </div>
                </div>
                <div className="features-grid">
                  <div className="feature">
                    <h4>Quick Data Capture</h4>
                    <p>Instantly capture attendee information from event badges.</p>
                  </div>
                  <div className="feature">
                    <h4>Seamless Integration</h4>
                    <p>Works with major event management platforms and badge providers.</p>
                  </div>
                  <div className="feature">
                    <h4>Low-Touch Collection</h4>
                    <p>Minimize friction while maximizing data quality at commercial events.</p>
                  </div>
                </div>
                <div className="capability-screenshot-showcase">
                  <h4>See It In Action</h4>
                  <p className="screenshot-caption">Scan a badge QR code and watch attendee details pre-fill the lead capture form instantly.</p>
                  <div className="screenshot-frame screenshot-frame--wide">
                    <img src={imgBadgeScan} alt="Badge scanning flow: empty form, QR code scan, pre-filled form" />
                  </div>
                </div>
              </div>
            )}

            {/* Seamless Experience */}
            {activeTab === 'seamless-experience' && (
              <div className="capability-content">
                <h3>Seamless Experience</h3>
                <p className="capability-intro">
                  Build comprehensive participant profiles over time without creating friction.
                </p>
                <div className="features-grid">
                  <div className="feature">
                    <h4>Registration Lookups</h4>
                    <p>When a participant registers at one activation, their profile can be quickly looked up at any subsequent activation—making it easy to verify details or track interactions.</p>
                  </div>
                  <div className="feature">
                    <h4>Progressive Profiling</h4>
                    <p>Build on previous responses over time for a more complete participant profile without asking the same questions twice.</p>
                  </div>
                  <div className="feature">
                    <h4>Connected Journey</h4>
                    <p>Track participants across multiple activations and touchpoints throughout your event.</p>
                  </div>
                  <div className="feature">
                    <h4>Quick Check-In</h4>
                    <p>Returning participants experience faster check-ins at subsequent activations.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Smarter Reporting */}
            {activeTab === 'reporting' && (
              <div className="capability-content">
                <h3>Smarter Reporting</h3>
                <p className="capability-intro">
                  Streamline experience without compromising insights.
                </p>
                <div className="features-grid">
                  <div className="feature">
                    <h4>Capture User Journeys</h4>
                    <p>Track the complete path participants take through your event, from first touchpoint to final interaction.</p>
                  </div>
                  <div className="feature">
                    <h4>Total Interactions</h4>
                    <p>The total number of times consumers engage with each activation, including revisits.</p>
                  </div>
                  <div className="feature">
                    <h4>Source of Registration</h4>
                    <p>Understand where consumers started their journey and which activations drive the most engagement.</p>
                  </div>
                  <div className="feature">
                    <h4>Unique Interactions</h4>
                    <p>Track individual unique engagements vs. repeat visits to understand behavior patterns.</p>
                  </div>
                </div>
                <div className="capability-screenshot-showcase">
                  <h4>Real Event Data</h4>
                  <p className="screenshot-caption">Interaction and registration breakdowns across every activation — visualized instantly.</p>
                  <div className="screenshot-grid">
                    <div className="screenshot-frame">
                      <img src={imgReportingPie} alt="Total Interactions and Source of Registration pie charts" />
                    </div>
                    <div className="screenshot-frame">
                      <img src={imgReportingFull} alt="Full reporting dashboard with survey response breakdowns" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Live Dashboards */}
            {activeTab === 'dashboards' && (
              <div className="capability-content">
                <h3>Live Dashboards</h3>
                <p className="capability-intro">
                  View real-time insights on registrations, interactions, and performance metrics.
                </p>
                <div className="features-grid">
                  <div className="feature">
                    <h4>Total Registrants</h4>
                    <p>All consumers who completed a survey regardless of activation.</p>
                  </div>
                  <div className="feature">
                    <h4>Email Opt-ins</h4>
                    <p>Track opt-in rates and qualified leads with full contact information.</p>
                  </div>
                  <div className="feature">
                    <h4>Customizable Targets</h4>
                    <p>Set and monitor goals in real-time throughout your event.</p>
                  </div>
                  <div className="feature">
                    <h4>Easy Export</h4>
                    <p>Export reports and graphs in your preferred format for further analysis.</p>
                  </div>
                </div>
                <div className="dashboard-features">
                  <h4>View Real-Time Insights On:</h4>
                  <ul>
                    <li>Registrations, interactions, pre-surveys, post-surveys</li>
                    <li>Contest and Marketing Opt-ins</li>
                    <li>Survey Response breakdowns</li>
                    <li>Sales Rep & Brand Ambassador Performance</li>
                    <li>Daily registrant trends and patterns</li>
                  </ul>
                </div>
                <div className="reporting-note">
                  <h4>Reporting Your Way</h4>
                  <p>Get the insights you need—whether it's daily details or total rollups, we've got you covered.</p>
                </div>
                <div className="capability-screenshot-showcase">
                  <h4>Live Dashboard Views</h4>
                  <p className="screenshot-caption">Monitor registrants, opt-in rates, survey scores, and daily trends — all in real time.</p>
                  <div className="screenshot-grid">
                    <div className="screenshot-frame">
                      <img src={imgDashboardGauges} alt="Total Registrants and Email Opt-ins gauges" />
                    </div>
                    <div className="screenshot-frame">
                      <img src={imgDashboardGoals} alt="Capability and passion score charts with daily registrant trends" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Custom Integrations */}
            {activeTab === 'integrations' && (
              <div className="capability-content">
                <h3>Custom Integrations</h3>
                <p className="capability-intro">
                  Built to connect with your existing systems and workflows.
                </p>
                <div className="features-grid">
                  <div className="feature">
                    <h4>Deliver Raw Data</h4>
                    <p>Receive data in your preferred format—CSV, JSON, XML, or custom specifications.</p>
                  </div>
                  <div className="feature">
                    <h4>API Connections</h4>
                    <p>Connect directly to your APIs for real-time data synchronization.</p>
                  </div>
                  <div className="feature">
                    <h4>CRM Sync</h4>
                    <p>Seamlessly sync with your CRM or database for immediate lead follow-up.</p>
                  </div>
                  <div className="feature">
                    <h4>Personalized Outreach</h4>
                    <p>Trigger personalized emails or SMS based on survey responses and expressed interests.</p>
                  </div>
                </div>
                <div className="integration-example">
                  <h4>Individual Outreach Example:</h4>
                  <div className="email-example">
                    <p><em>"Hi John, Thanks for spending time with [Brand] at [Event] yesterday — we hope you enjoyed your test drive experience! You expressed interest in our commercial engines, and we're here to help you move forward. As a thank you, we're offering an exclusive show promo: $50 off your next purchase!"</em></p>
                    <p><strong>Personalized communications based on survey responses</strong></p>
                  </div>
                </div>
                <div className="capability-screenshot-showcase">
                  <h4>Triggered SMS in the Wild</h4>
                  <p className="screenshot-caption">Attendees receive personalized product info and event photos seconds after their interaction.</p>
                  <div className="screenshot-frame screenshot-frame--phone">
                    <img src={imgSmsOutreach} alt="Triggered SMS messages sent to attendees after event interaction" />
                  </div>
                </div>
              </div>
            )}

            {/* iOS Native App */}
            {activeTab === 'ios-app' && (
              <div className="capability-content">
                <h3>iOS Native App</h3>
                <p className="capability-intro">
                  Powerful mobile solution for on-the-ground teams at events.
                </p>
                <div className="features-grid">
                  <div className="feature">
                    <h4>Works On or Offline*</h4>
                    <p>Securely capture data even without internet connectivity, syncing when connection returns.</p>
                  </div>
                  <div className="feature">
                    <h4>Multi-Language Capabilities</h4>
                    <p>Support for multiple languages to accommodate diverse event audiences.</p>
                  </div>
                  <div className="feature">
                    <h4>Team Performance Tracking</h4>
                    <p>Monitor onsite team member performance including Sales Reps and Product Specialists.</p>
                  </div>
                  <div className="feature">
                    <h4>Customized Data Capture Forms</h4>
                    <p>Fully customizable forms tailored to your specific data collection needs.</p>
                  </div>
                  <div className="feature">
                    <h4>Triggered Communications</h4>
                    <p>Send automated email or SMS with content relevant to consumer interests.</p>
                  </div>
                  <div className="feature">
                    <h4>Real-Time Leaderboards</h4>
                    <p>Motivate teams with live performance rankings and metrics.</p>
                  </div>
                </div>
                <div className="performance-example">
                  <h4>Team Performance Example:</h4>
                  <div className="leaderboard">
                    <div className="leaderboard-item">
                      <span>Kristina Samuels</span>
                      <span className="score">102</span>
                    </div>
                    <div className="leaderboard-item">
                      <span>Jenny Werner</span>
                      <span className="score">83</span>
                    </div>
                    <div className="leaderboard-item">
                      <span>Francois Delaney</span>
                      <span className="score">82</span>
                    </div>
                    <div className="leaderboard-item">
                      <span>Daniel Smith</span>
                      <span className="score">79</span>
                    </div>
                  </div>
                  <p className="note">*Some features limited in offline mode</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;

